/**
 * The server's machine code for a failed request.
 *
 * `ApiError.message` prefers the server's human sentence whenever one is sent
 * (see `request()` in `./api.ts`), so it is the wrong thing to compare against
 * a code -- or to key a lookup table with. The code itself lives in the parsed
 * body, which `ApiError` has carried all along.
 *
 * Every code-keyed branch in the app reads it through here, so there is one
 * place to change if the wire shape ever grows a second spelling.
 */
import { ApiError } from "./api.ts";

export function apiErrorCode(error: ApiError): string | null {
  const body = error.body;
  if (!body || typeof body !== "object" || !("error" in body)) return null;
  const code = (body as { error: unknown }).error;
  return typeof code === "string" ? code : null;
}

/**
 * A numeric fact the error body carries alongside its code.
 *
 * Some errors have to be spelled out with one of the server's own numbers --
 * the help daily cap is the first -- and `result` is null on those responses,
 * so the body is the only place the number can be. Same parse as
 * `apiErrorCode`, deliberately: one reader of `ApiError.body`, not two.
 */
export function apiErrorNumber(error: ApiError, field: string): number | null {
  const body = error.body;
  if (!body || typeof body !== "object" || !(field in body)) return null;
  const value = (body as Record<string, unknown>)[field];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
